import React, { useEffect, useState, useRef } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makePatchRequestOnUserService, makePOSTRequestOnUserService } from "../../Api/api";

export const relationshipOptions = [
    { value: "father", label: "Father" },
    { value: "mother", label: "Mother" },
    { value: "court_appointed_legal_guardian", label: "Court Appointed Legal Guardian" },
    { value: "aunt", label: "Aunt" },
    { value: "brother_in_law", label: "Brother-in-law" },
    { value: "brother", label: "Brother" },
    { value: "daughter", label: "Daughter" },
    { value: "daughter_in_law", label: "Daughter-in-law" },
    { value: "father_in_law", label: "Father-in-law" },
    { value: "grand_daughter", label: "Grand Daughter" },
    { value: "grand_father", label: "Grand Father" },
    { value: "grand_mother", label: "Grand Mother" },
    { value: "grand_son", label: "Grand Son" },
    { value: "mother_in_law", label: "Mother-in-law" },
    { value: "nephew", label: "Nephew" },
    { value: "niece", label: "Niece" },
    { value: "sister", label: "Sister" },
    { value: "sister_in_law", label: "Sister-in-law" },
    { value: "son", label: "Son" },
    { value: "son_in_law", label: "Son-in-law" },
    { value: "spouse", label: "Spouse" },
    { value: "uncle", label: "Uncle" },
    { value: "others", label: "Others" },
];

interface InvestorProfileEditProps {
    data: any;
    onBankUpdateSuccess?: () => void;
    onAddressUpdateSuccess?: () => void;
    onDematUpdateSuccess?: () => void;
    onContactUpdateSuccess?: () => void;
    onOverseasAddressUpdateSuccess?: () => void;
    onNomineeUpdateSuccess?: () => void;
}

const Field = ({
    label,
    value,
    onChange,
    disabled = false,
}: {
    label: string;
    value: any;
    onChange?: (value: string) => void;
    disabled?: boolean;
}) => (
    <Grid item xs={12} sm={6} md={4}>
        <TextField
            fullWidth
            size="small"
            label={label}
            value={value ?? ""}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
            sx={{
                "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: disabled ? "#f7f7f8" : "#fff",
                },
                "& .MuiInputLabel-root": {
                    fontSize: "14px",
                },
            }}
        />
    </Grid>
);

const SelectField = ({
    label,
    value,
    section,
    field,
    options,
    onSelectChange,
}: {
    label: string;
    value: string | undefined;
    section: string;
    field: string;
    options: { value: string; label: string }[];
    onSelectChange: (section: string, field: string, event: SelectChangeEvent<string>) => void;
}) => (
    <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size="small">
            <InputLabel id={`${section}-${field}-label`} shrink>
                {label}
            </InputLabel>
            <Select
                labelId={`${section}-${field}-label`}
                label={label}
                notched
                value={value || ""}
                onChange={(e) => onSelectChange(section, field, e)}
                displayEmpty
                MenuProps={{
                    disablePortal: true,
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                        },
                    },
                }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                    },
                }}
            >
                <MenuItem value="" disabled>
                    <em>Select {label}</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </Grid>
);

const Section = ({
    title,
    children,
    defaultExpanded = true,
}: {
    title: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
}) => (
    <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
            mb: 2,
            borderRadius: "8px !important",
            border: "1px solid #e4e5eb",
            boxShadow: "none",
            "&:before": {
                display: "none",
            },
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
                backgroundColor: "#f7f7f9",
                minHeight: 64,
                borderRadius: "8px",
                "&.Mui-expanded": {
                    minHeight: 64,
                    borderBottom: "1px solid #e4e5eb",
                },
                "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                    margin: "12px 0",
                },
            }}
        >
            <Typography
                sx={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#262349",
                }}
            >
                {title}
            </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.5, backgroundColor: "#fff" }}>
            {children}
        </AccordionDetails>
    </Accordion>
);

const accountTypeOptions = [
    { value: "savings", label: "Savings" },
    { value: "current", label: "Current" },
    { value: "nre", label: "NRE" },
    { value: "nro", label: "NRO" },
];

const addressNatureOptions = [
    { value: "residential", label: "Residential" },
    { value: "business_location", label: "Business Location" },
];

const InvestorProfileEdit = ({
    data,
    onBankUpdateSuccess,
    onAddressUpdateSuccess,
    onDematUpdateSuccess,
    onContactUpdateSuccess,
    onOverseasAddressUpdateSuccess,
    onNomineeUpdateSuccess,
}: InvestorProfileEditProps) => {
    const [formData, setFormData] = useState<any>({});
    const [bankUpdating, setBankUpdating] = useState(false);
    const [addressUpdating, setAddressUpdating] = useState(false);
    const [dematUpdating, setDematUpdating] = useState(false);
    const [contactUpdating, setContactUpdating] = useState(false);
    const [overseasAddressUpdating, setOverseasAddressUpdating] = useState(false);
    const [nomineeUpdating, setNomineeUpdating] = useState(false);

    const isInitialized = useRef(false);

    const toastr = require("toastr");

    useEffect(() => {
        if (data && !isInitialized.current) {
            setFormData(JSON.parse(JSON.stringify(data)));
            isInitialized.current = true;
        }
    }, [data]);

    useEffect(() => {
        if (data?.user) {
            if (formData?.user && formData.user !== data.user) {
                isInitialized.current = false;
                setFormData(JSON.parse(JSON.stringify(data)));
                isInitialized.current = true;
            }
        }
    }, [data?.user]);

    const handleChange = (
        section: string,
        field: string,
        value: string
    ) => {
        setFormData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev?.[section],
                [field]: value,
            },
        }));
    };

    const handleSelectChange = (
        section: string,
        field: string,
        event: SelectChangeEvent<string>
    ) => {
        event.stopPropagation();

        const value = event.target.value;
        setFormData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev?.[section],
                [field]: value,
            },
        }));
    };

    const getErrorMessage = (error: any, defaultMessage: string) => {
        return (
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            defaultMessage
        );
    };

    const updateBankAccount = async () => {
        try {
            setBankUpdating(true);

            const payload = {
                user_uuid: formData?.user,
                profile: formData?.bank_details?.profile || "",
                primary_account_holder_name:
                    formData?.bank_details?.primary_account_holder_name || "",
                account_number: formData?.bank_details?.account_number || "",
                type: formData?.bank_details?.type || "",
                ifsc_code: formData?.bank_details?.ifsc_code || "",
            };

            const response = await makePatchRequestOnUserService(
                "/mutual_fund/bank_account/update/",
                payload
            );

            if (response?.status === 200 && response?.data?.success) {
                toastr.success(
                    response?.data?.message || "Bank account updated successfully"
                );
                onBankUpdateSuccess?.();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Bank account update failed"
                );
            }
        } catch (error: any) {
            console.error("Bank account update error:", error);
            toastr.error(getErrorMessage(error, "Bank account update failed"));
        } finally {
            setBankUpdating(false);
        }
    };

    const removeEmptyFields = (obj: Record<string, any>): Record<string, any> => {
        const result: Record<string, any> = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (value !== "" && value !== null && value !== undefined) {
                result[key] = value;
            }
        });
        return result;
    };

    const updateAddress = async () => {
        try {
            setAddressUpdating(true);

            const rawPayload = {
                user_uuid: formData?.user,
                profile: formData?.address?.profile || "",
                line1: formData?.address?.line1 || "",
                line2: formData?.address?.line2 || "",
                line3: formData?.address?.line3 || "",
                country: formData?.address?.country || "",
                postal_code: formData?.address?.postal_code || "",
                nature: formData?.address?.nature || "",
            };

            const payload = removeEmptyFields(rawPayload);

            const response = await makePatchRequestOnUserService(
                "/mutual_fund/address/update/",
                payload
            );

            if (response?.status === 200 && response?.data?.success) {
                toastr.success(
                    response?.data?.message || "Address updated successfully"
                );
                onAddressUpdateSuccess?.();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Address update failed"
                );
            }
        } catch (error: any) {
            console.error("Address update error:", error);
            toastr.error(getErrorMessage(error, "Address update failed"));
        } finally {
            setAddressUpdating(false);
        }
    };

    const updateDematAccount = async () => {
        try {
            setDematUpdating(true);

            const payload = {
                user_uuid: formData?.user,
                client_id: formData?.demat_details?.client_id || "",
                dp_id: formData?.demat_details?.dp_id || "",
            };

            if (!payload.user_uuid) {
                toastr.error("User UUID is required");
                return;
            }

            if (!payload.client_id) {
                toastr.error("Client ID is required");
                return;
            }

            if (!payload.dp_id) {
                toastr.error("DP ID is required");
                return;
            }

            const response = await makePatchRequestOnUserService(
                "/mutual_fund/demat_account/update/",
                payload
            );

            if (response?.status === 200 && response?.data?.success) {
                toastr.success(
                    response?.data?.message ||
                    "Demat account updated successfully"
                );
                onDematUpdateSuccess?.();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Demat account update failed"
                );
            }
        } catch (error: any) {
            console.error("Demat account update error:", error);

            toastr.error(
                getErrorMessage(
                    error,
                    "Demat account update failed"
                )
            );
        } finally {
            setDematUpdating(false);
        }
    };

    const updateContactDetails = async () => {
        if (!formData?.user) {
            toastr.error("User UUID is required");
            return;
        }

        const oldEmail = data?.email_address?.email || "";
        const newEmail = formData?.email_address?.email || "";

        const oldPhone = data?.phone_number?.number || "";
        const newPhone = formData?.phone_number?.number || "";

        const emailChanged = oldEmail.trim() !== newEmail.trim();
        const phoneChanged = oldPhone.trim() !== newPhone.trim();

        if (!emailChanged && !phoneChanged) {
            toastr.info("No contact details changes found");
            return;
        }
        try {
            setContactUpdating(true);

            if (emailChanged) {
                const emailPayload = {
                    user_uuid: formData.user,
                    email_id: newEmail.trim(),
                };

                const emailResponse = await makePatchRequestOnUserService(
                    "/mutual_fund/email_address/update/",
                    emailPayload
                );

                if (
                    emailResponse?.status !== 200 ||
                    !emailResponse?.data?.success
                ) {
                    throw new Error(
                        emailResponse?.data?.error ||
                        emailResponse?.data?.message ||
                        "Email address update failed"
                    );
                }

                toastr.success(
                    emailResponse?.data?.message ||
                    "Email address updated successfully"
                );
            }
            if (phoneChanged) {
                const phonePayload = {
                    user_uuid: formData.user,
                    phone_number: newPhone.trim(),
                };

                const phoneResponse = await makePatchRequestOnUserService(
                    "/mutual_fund/phone_number/update/",
                    phonePayload
                );
                if (
                    phoneResponse?.status !== 200 ||
                    !phoneResponse?.data?.success
                ) {
                    throw new Error(
                        phoneResponse?.data?.error ||
                        phoneResponse?.data?.message ||
                        "Phone number update failed"
                    );
                }
                toastr.success(
                    phoneResponse?.data?.message ||
                    "Phone number updated successfully"
                );
            }
            onContactUpdateSuccess?.();
        } catch (error: any) {
            console.error("Contact details update error:", error);

            toastr.error(
                getErrorMessage(
                    error,
                    "Contact details update failed"
                )
            );
        } finally {
            setContactUpdating(false);
        }
    };

    const updateOverseasAddress = async () => {
        try {
            setOverseasAddressUpdating(true);

            const rawPayload = {
                user_uuid: formData?.user,
                line1: formData?.overseas_address?.line1 || "",
                line2: formData?.overseas_address?.line2 || "",
                line3: formData?.overseas_address?.line3 || "",
                country: formData?.overseas_address?.country || "",
                postal_code: formData?.overseas_address?.postal_code || "",
                nature: formData?.overseas_address?.nature || "",
                city: formData?.overseas_address?.city || "",
            };

            const payload = removeEmptyFields(rawPayload);

            if (!payload.user_uuid) {
                toastr.error("User UUID is required");
                return;
            }

            const requiredFields = [
                "line1",
                "country",
                "postal_code",
                "nature",
                "city",
            ];

            const missingFields = requiredFields.filter(
                (field) =>
                    !payload[field] ||
                    String(payload[field]).trim() === ""
            );

            if (missingFields.length > 0) {
                toastr.error(
                    `Missing required fields: ${missingFields.join(", ")}`
                );
                return;
            }

            const response = await makePatchRequestOnUserService(
                "/mutual_fund/overseas_address/update/",
                payload
            );

            if (
                response?.status === 200 &&
                response?.data?.success
            ) {
                toastr.success(
                    response?.data?.message ||
                    "Overseas address updated successfully"
                );

                onOverseasAddressUpdateSuccess?.();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Overseas address update failed"
                );
            }
        } catch (error: any) {
            console.error(
                "Overseas address update error:",
                error
            );

            toastr.error(
                getErrorMessage(
                    error,
                    "Overseas address update failed"
                )
            );
        } finally {
            setOverseasAddressUpdating(false);
        }
    };

    const updateNomineeDetails = async () => {
        if (!formData?.user) {
            toastr.error("User UUID is required");
            return;
        }

        const nominee = formData?.nominee_details;

        if (!nominee) {
            toastr.error("Nominee details not found");
            return;
        }

        const name = nominee?.name?.trim() || "";
        const dateOfBirth = nominee?.date_of_birth || "";
        const relationship = nominee?.relationship || "";

        if (!name) {
            toastr.error("Nominee name is required");
            return;
        }

        if (!dateOfBirth) {
            toastr.error("Nominee date of birth is required");
            return;
        }

        if (!relationship) {
            toastr.error("Nominee relationship is required");
            return;
        }

        const formattedDateOfBirth = dateOfBirth.slice(0, 10);

        const calculateIsMinor = (dateOfBirth: string) => {
            const dob = new Date(`${dateOfBirth}T00:00:00`);
            const today = new Date();

            let age =
                today.getFullYear() -
                dob.getFullYear();

            const monthDiff =
                today.getMonth() -
                dob.getMonth();

            if (
                monthDiff < 0 ||
                (
                    monthDiff === 0 &&
                    today.getDate() < dob.getDate()
                )
            ) {
                age--;
            }

            return age < 18;
        };

        const isNomineeMinor = calculateIsMinor(
            formattedDateOfBirth
        );

        let emailAddress = "";
        let phoneNumber = "";
        let aadhaarNumber = "";

        if (isNomineeMinor) {
            emailAddress =
                nominee?.guardian_email_address?.trim() || "";

            phoneNumber =
                nominee?.guardian_phone_number?.number?.trim() || "";

            aadhaarNumber =
                nominee?.guardian_aadhaar_number?.trim() || "";
        } else {
            emailAddress =
                nominee?.email_address?.trim() || "";

            phoneNumber =
                nominee?.phone_number?.number?.trim() || "";

            aadhaarNumber =
                nominee?.aadhaar_number?.trim() || "";
        }

        const nomineeAddress = isNomineeMinor
            ? nominee?.guardian_address
            : nominee?.address;

        const payload = {
            user_uuid: formData.user,
            profile: nominee?.profile || "",
            name: name,
            date_of_birth: formattedDateOfBirth,
            relationship: relationship,
            is_nominee_minor: isNomineeMinor,
            aadhaar_number: aadhaarNumber,
            guardian_name: isNomineeMinor
                ? nominee?.guardian_name?.trim() || ""
                : "",
            email_address: emailAddress,
            phone_number: phoneNumber,
            line1: nomineeAddress?.line1?.trim() || "",
            line2: nomineeAddress?.line2?.trim() || "",
            line3: nomineeAddress?.line3?.trim() || "",
            country: nomineeAddress?.country?.trim() || "",
            pin_code: nomineeAddress?.postal_code?.trim() || "",
        };

        console.log(
            "Nominee Update Payload:",
            payload
        );

        // ================= VALIDATION =================

        if (!payload.aadhaar_number) {
            toastr.error(
                isNomineeMinor
                    ? "Guardian Aadhaar number is required"
                    : "Aadhaar number is required"
            );
            return;
        }

        if (!/^\d{4}$/.test(payload.aadhaar_number)) {
            toastr.error(
                "Please enter the last 4 digits of Aadhaar number"
            );
            return;
        }

        if (!payload.email_address) {
            toastr.error(
                isNomineeMinor
                    ? "Guardian email address is required"
                    : "Nominee email address is required"
            );
            return;
        }

        if (!payload.phone_number) {
            toastr.error(
                isNomineeMinor
                    ? "Guardian phone number is required"
                    : "Nominee phone number is required"
            );
            return;
        }

        if (!payload.line1) {
            toastr.error("Address Line 1 is required");
            return;
        }

        if (!payload.country) {
            toastr.error("Country is required");
            return;
        }

        if (!payload.pin_code) {
            toastr.error("Postal code is required");
            return;
        }

        if (isNomineeMinor && !payload.guardian_name) {
            toastr.error(
                "Guardian name is required for a minor nominee"
            );
            return;
        }

        try {
            setNomineeUpdating(true);

            const response =
                await makePOSTRequestOnUserService(
                    "/mutual_fund/nominee_update_by_admin/",
                    payload
                );

            if (
                response?.status === 200 &&
                response?.data?.success
            ) {
                toastr.success(
                    response?.data?.message ||
                    "Nominee details updated successfully"
                );

                onNomineeUpdateSuccess?.();
            } else {
                toastr.error(
                    response?.data?.error ||
                    response?.data?.message ||
                    "Nominee details update failed"
                );
            }
        } catch (error: any) {
            console.error(
                "Nominee details update error:",
                error
            );

            toastr.error(
                getErrorMessage(
                    error,
                    "Nominee details update failed"
                )
            );
        } finally {
            setNomineeUpdating(false);
        }
    };

    if (!data) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>No data available</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                backgroundColor: "#f5f6fa",
                p: { xs: 1, sm: 2 },
            }}
        >
            {/* ================= BANK DETAILS ================= */}
            <Section title="Bank Details">
                <Grid container spacing={2}>
                    <Field
                        label="Account Holder Name"
                        value={formData?.bank_details?.primary_account_holder_name}
                        onChange={(value) =>
                            handleChange("bank_details", "primary_account_holder_name", value)
                        }
                    />
                    <Field
                        label="Account Number"
                        value={formData?.bank_details?.account_number}
                        onChange={(value) =>
                            handleChange("bank_details", "account_number", value)
                        }
                    />
                    <Field
                        label="Bank Name"
                        value={formData?.bank_details?.bank_name}
                        onChange={(value) =>
                            handleChange("bank_details", "bank_name", value)
                        }
                    />

                    <SelectField
                        label="Account Type"
                        value={formData?.bank_details?.type}
                        section="bank_details"
                        field="type"
                        options={accountTypeOptions}
                        onSelectChange={handleSelectChange}
                    />

                    <Field
                        label="IFSC Code"
                        value={formData?.bank_details?.ifsc_code}
                        onChange={(value) =>
                            handleChange("bank_details", "ifsc_code", value)
                        }
                    />
                    <Field
                        label="Branch Name"
                        value={formData?.bank_details?.branch_name}
                        disabled
                    />
                    <Field
                        label="Branch City"
                        value={formData?.bank_details?.branch_city}
                        disabled
                    />
                    <Field
                        label="Branch District"
                        value={formData?.bank_details?.branch_district}
                        disabled
                    />
                    <Field
                        label="Branch State"
                        value={formData?.bank_details?.branch_state}
                        disabled
                    />
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            disabled
                            size="small"
                            multiline
                            minRows={2}
                            label="Branch Address"
                            value={formData?.bank_details?.branch_address || ""}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        onClick={updateBankAccount}
                        disabled={bankUpdating}
                        sx={{
                            backgroundColor: "#262349",
                            textTransform: "none",
                            px: 3,
                            minWidth: 180,
                            "&:hover": { backgroundColor: "#1d1a38" },
                        }}
                    >
                        {bankUpdating ? (
                            <>
                                <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                                Updating...
                            </>
                        ) : (
                            "Update Bank Details"
                        )}
                    </Button>
                </Box>
            </Section>

            {/* ================= ADDRESS DETAILS ================= */}
            <Section title="Address Details">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            label="Address Line 1"
                            value={formData?.address?.line1 || ""}
                            onChange={(e) =>
                                handleChange("address", "line1", e.target.value)
                            }
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            label="Address Line 2"
                            value={formData?.address?.line2 || ""}
                            onChange={(e) =>
                                handleChange("address", "line2", e.target.value)
                            }
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            label="Address Line 3"
                            value={formData?.address?.line3 || ""}
                            onChange={(e) =>
                                handleChange("address", "line3", e.target.value)
                            }
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                        />
                    </Grid>
                    <Field label="City" value={formData?.address?.city} disabled />
                    <Field label="State" value={formData?.address?.state} disabled />
                    <Field
                        label="Country"
                        value={formData?.address?.country}
                        onChange={(value) => handleChange("address", "country", value)}
                    />
                    <Field
                        label="Postal Code"
                        value={formData?.address?.postal_code}
                        onChange={(value) => handleChange("address", "postal_code", value)}
                    />

                    <SelectField
                        label="Nature"
                        value={formData?.address?.nature}
                        section="address"
                        field="nature"
                        options={addressNatureOptions}
                        onSelectChange={handleSelectChange}
                    />
                </Grid>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        onClick={updateAddress}
                        disabled={addressUpdating}
                        sx={{
                            backgroundColor: "#262349",
                            textTransform: "none",
                            px: 3,
                            minWidth: 180,
                            "&:hover": { backgroundColor: "#1d1a38" },
                        }}
                    >
                        {addressUpdating ? (
                            <>
                                <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                                Updating...
                            </>
                        ) : (
                            "Update Address"
                        )}
                    </Button>
                </Box>
            </Section>

            {/* ================= DEMAT DETAILS ================= */}
            <Section title="Demat Details">
                <Grid container spacing={2}>
                    <Field
                        label="Client ID"
                        value={formData?.demat_details?.client_id}
                        onChange={(value) =>
                            handleChange(
                                "demat_details",
                                "client_id",
                                value
                            )
                        }
                    />

                    <Field
                        label="DP ID"
                        value={formData?.demat_details?.dp_id}
                        onChange={(value) =>
                            handleChange(
                                "demat_details",
                                "dp_id",
                                value
                            )
                        }
                    />
                </Grid>

                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={updateDematAccount}
                        disabled={dematUpdating}
                        sx={{
                            backgroundColor: "#262349",
                            textTransform: "none",
                            px: 3,
                            minWidth: 180,
                            "&:hover": {
                                backgroundColor: "#1d1a38",
                            },
                        }}
                    >
                        {dematUpdating ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    sx={{
                                        color: "#fff",
                                        mr: 1,
                                    }}
                                />
                                Updating...
                            </>
                        ) : (
                            "Update Demat Details"
                        )}
                    </Button>
                </Box>
            </Section>

            {/* ================= CONTACT DETAILS ================= */}
            <Section title="Contact Details">
                <Grid container spacing={2}>
                    <Field
                        label="Email"
                        value={formData?.email_address?.email}
                        onChange={(value) => handleChange("email_address", "email", value)}
                    />
                    <Field
                        label="ISD"
                        value={formData?.phone_number?.isd}
                        disabled
                    />
                    <Field
                        label="Phone Number"
                        value={formData?.phone_number?.number}
                        onChange={(value) => handleChange("phone_number", "number", value)}
                    />
                </Grid>
                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={updateContactDetails}
                        disabled={contactUpdating}
                        sx={{
                            backgroundColor: "#262349",
                            textTransform: "none",
                            px: 3,
                            minWidth: 200,
                            "&:hover": {
                                backgroundColor: "#1d1a38",
                            },
                        }}
                    >
                        {contactUpdating ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    sx={{
                                        color: "#fff",
                                        mr: 1,
                                    }}
                                />
                                Updating...
                            </>
                        ) : (
                            "Update Contact Details"
                        )}
                    </Button>
                </Box>
            </Section>

            {/* ================= OVERSEAS ADDRESS DETAILS ================= */}

            {formData?.overseas_address_id &&
                formData?.overseas_address && (
                    <Section title="Overseas Address Details">
                        <Grid container spacing={2}>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    label="Address Line 1"
                                    value={
                                        formData.overseas_address.line1 || ""
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "overseas_address",
                                            "line1",
                                            e.target.value
                                        )
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    label="Address Line 2"
                                    value={
                                        formData.overseas_address.line2 || ""
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "overseas_address",
                                            "line2",
                                            e.target.value
                                        )
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    label="Address Line 3"
                                    value={
                                        formData.overseas_address.line3 || ""
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "overseas_address",
                                            "line3",
                                            e.target.value
                                        )
                                    }
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                        },
                                    }}
                                />
                            </Grid>

                            <Field
                                label="City"
                                value={formData.overseas_address.city}
                                onChange={(value) =>
                                    handleChange(
                                        "overseas_address",
                                        "city",
                                        value
                                    )
                                }
                            />

                            <Field
                                label="Country"
                                value={formData.overseas_address.country}
                                onChange={(value) =>
                                    handleChange(
                                        "overseas_address",
                                        "country",
                                        value
                                    )
                                }
                            />

                            <Field
                                label="Postal Code"
                                value={formData.overseas_address.postal_code}
                                onChange={(value) =>
                                    handleChange(
                                        "overseas_address",
                                        "postal_code",
                                        value
                                    )
                                }
                            />

                            <SelectField
                                label="Nature"
                                value={formData.overseas_address.nature}
                                section="overseas_address"
                                field="nature"
                                options={addressNatureOptions}
                                onSelectChange={handleSelectChange}
                            />

                        </Grid>

                        <Box
                            sx={{
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={updateOverseasAddress}
                                disabled={overseasAddressUpdating}
                                sx={{
                                    backgroundColor: "#262349",
                                    textTransform: "none",
                                    px: 3,
                                    minWidth: 220,
                                    "&:hover": {
                                        backgroundColor: "#1d1a38",
                                    },
                                }}
                            >
                                {overseasAddressUpdating ? (
                                    <>
                                        <CircularProgress
                                            size={18}
                                            sx={{
                                                color: "#fff",
                                                mr: 1,
                                            }}
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    "Update Overseas Address"
                                )}
                            </Button>
                        </Box>
                    </Section>
                )}

            {/* ================= NOMINEE DETAILS ================= */}

            {(() => {
                const nominee = formData?.nominee_details;

                const calculateIsMinor = (
                    dateOfBirth: string | undefined
                ) => {
                    if (!dateOfBirth) return false;

                    const dob = new Date(dateOfBirth);
                    const today = new Date();

                    let age =
                        today.getFullYear() -
                        dob.getFullYear();

                    const monthDiff =
                        today.getMonth() -
                        dob.getMonth();

                    if (
                        monthDiff < 0 ||
                        (
                            monthDiff === 0 &&
                            today.getDate() < dob.getDate()
                        )
                    ) {
                        age--;
                    }

                    return age < 18;
                };

                const isNomineeMinor = calculateIsMinor(
                    nominee?.date_of_birth
                );

                return (
                    <Section title="Nominee Details">
                        <Grid container spacing={2}>

                            {/* ================= COMMON DETAILS ================= */}

                            <Field
                                label="Name"
                                value={nominee?.name}
                                onChange={(value) =>
                                    handleChange(
                                        "nominee_details",
                                        "name",
                                        value
                                    )
                                }
                            />

                            <SelectField
                                label="Relationship"
                                value={nominee?.relationship}
                                section="nominee_details"
                                field="relationship"
                                options={relationshipOptions}
                                onSelectChange={handleSelectChange}
                            />

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date of Birth"
                                    value={
                                        nominee?.date_of_birth
                                            ? nominee.date_of_birth.slice(0, 10)
                                            : ""
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            "nominee_details",
                                            "date_of_birth",
                                            e.target.value
                                        )
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                        },
                                    }}
                                />
                            </Grid>

                            {/* ================= MINOR ================= */}

                            {isNomineeMinor ? (
                                <>
                                    <Field
                                        label="Guardian Name"
                                        value={nominee?.guardian_name}
                                        onChange={(value) =>
                                            handleChange(
                                                "nominee_details",
                                                "guardian_name",
                                                value
                                            )
                                        }
                                    />

                                    <Field
                                        label="Guardian Phone Number"
                                        value={nominee?.guardian_phone_number?.number}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    guardian_phone_number: {
                                                        ...prev?.nominee_details?.guardian_phone_number,
                                                        number: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />

                                    <Field
                                        label="Guardian Aadhaar (Last 4 Digits)"
                                        value={nominee?.guardian_aadhaar_number}
                                        onChange={(value) =>
                                            handleChange(
                                                "nominee_details",
                                                "guardian_aadhaar_number",
                                                value
                                            )
                                        }
                                    />

                                    <Field
                                        label="Guardian Email Address"
                                        value={nominee?.guardian_email_address}
                                        onChange={(value) =>
                                            handleChange(
                                                "nominee_details",
                                                "guardian_email_address",
                                                value
                                            )
                                        }
                                    />

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 1"
                                            value={nominee?.guardian_address?.line1 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        guardian_address: {
                                                            ...prev?.nominee_details?.guardian_address,
                                                            line1: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 2"
                                            value={nominee?.guardian_address?.line2 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        guardian_address: {
                                                            ...prev?.nominee_details?.guardian_address,
                                                            line2: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 3"
                                            value={nominee?.guardian_address?.line3 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        guardian_address: {
                                                            ...prev?.nominee_details?.guardian_address,
                                                            line3: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Field
                                        label="City"
                                        value={nominee?.guardian_address?.city}
                                        disabled
                                    />

                                    <Field
                                        label="State"
                                        value={nominee?.guardian_address?.state}
                                        disabled
                                    />

                                    <Field
                                        label="Country"
                                        value={nominee?.guardian_address?.country}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    guardian_address: {
                                                        ...prev?.nominee_details?.guardian_address,
                                                        country: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />

                                    <Field
                                        label="Postal Code"
                                        value={nominee?.guardian_address?.postal_code}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    guardian_address: {
                                                        ...prev?.nominee_details?.guardian_address,
                                                        postal_code: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    {/* ================= NON-MINOR ================= */}

                                    <Field
                                        label="Phone Number"
                                        value={nominee?.phone_number?.number}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    phone_number: {
                                                        ...prev?.nominee_details?.phone_number,
                                                        number: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />

                                    <Field
                                        label="Aadhaar (Last 4 Digits)"
                                        value={nominee?.aadhaar_number}
                                        onChange={(value) =>
                                            handleChange(
                                                "nominee_details",
                                                "aadhaar_number",
                                                value
                                            )
                                        }
                                    />

                                    <Field
                                        label="Email Address"
                                        value={nominee?.email_address}
                                        onChange={(value) =>
                                            handleChange(
                                                "nominee_details",
                                                "email_address",
                                                value
                                            )
                                        }
                                    />

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 1"
                                            value={nominee?.address?.line1 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        address: {
                                                            ...prev?.nominee_details?.address,
                                                            line1: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 2"
                                            value={nominee?.address?.line2 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        address: {
                                                            ...prev?.nominee_details?.address,
                                                            line2: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            label="Address Line 3"
                                            value={nominee?.address?.line3 || ""}
                                            onChange={(e) =>
                                                setFormData((prev: any) => ({
                                                    ...prev,
                                                    nominee_details: {
                                                        ...prev?.nominee_details,
                                                        address: {
                                                            ...prev?.nominee_details?.address,
                                                            line3: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Field
                                        label="City"
                                        value={nominee?.address?.city}
                                        disabled
                                    />

                                    <Field
                                        label="State"
                                        value={nominee?.address?.state}
                                        disabled
                                    />

                                    <Field
                                        label="Country"
                                        value={nominee?.address?.country}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    address: {
                                                        ...prev?.nominee_details?.address,
                                                        country: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />

                                    <Field
                                        label="Postal Code"
                                        value={nominee?.address?.postal_code}
                                        onChange={(value) =>
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                nominee_details: {
                                                    ...prev?.nominee_details,
                                                    address: {
                                                        ...prev?.nominee_details?.address,
                                                        postal_code: value,
                                                    },
                                                },
                                            }))
                                        }
                                    />
                                </>
                            )}
                        </Grid>

                        <Box
                            sx={{
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={updateNomineeDetails}
                                disabled={nomineeUpdating}
                                sx={{
                                    backgroundColor: "#262349",
                                    textTransform: "none",
                                    px: 3,
                                    minWidth: 220,
                                    "&:hover": {
                                        backgroundColor: "#1d1a38",
                                    },
                                }}
                            >
                                {nomineeUpdating ? (
                                    <>
                                        <CircularProgress
                                            size={18}
                                            sx={{
                                                color: "#fff",
                                                mr: 1,
                                            }}
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    "Update Nominee Details"
                                )}
                            </Button>
                        </Box>
                    </Section>
                );
            })()}

            {/* ================= FILE DETAILS ================= */}
            {formData?.file_details?.id &&
                formData?.file_details?.url && (
                    <Section title="File Details">
                        <Grid container spacing={2}>

                            <Field
                                label="File Name"
                                value={formData.file_details.filename}
                                disabled
                            />

                            <Field
                                label="Content Type"
                                value={formData.file_details.content_type}
                                disabled
                            />

                            {/* ================= IMAGE PREVIEW ================= */}
                            {formData.file_details.content_type?.startsWith("image/") && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            border: "1px solid #e4e5eb",
                                            borderRadius: 2,
                                            p: 2,
                                            backgroundColor: "#f7f7f9",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={formData.file_details.url}
                                            alt={
                                                formData.file_details.filename ||
                                                "Uploaded file"
                                            }
                                            sx={{
                                                maxWidth: "100%",
                                                maxHeight: 500,
                                                objectFit: "contain",
                                                borderRadius: 1,
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            )}

                            {/* ================= PDF PREVIEW ================= */}
                            {formData.file_details.content_type === "application/pdf" && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            border: "1px solid #e4e5eb",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            backgroundColor: "#f7f7f9",
                                        }}
                                    >
                                        <Box
                                            component="iframe"
                                            src={formData.file_details.url}
                                            title={
                                                formData.file_details.filename ||
                                                "PDF Preview"
                                            }
                                            sx={{
                                                width: "100%",
                                                height: 600,
                                                border: "none",
                                                display: "block",
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            )}

                        </Grid>
                    </Section>
                )}

        </Box>
    );
};

export default InvestorProfileEdit;